import React, { useState, useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import ImageZoomPin from "../../../Components/PinOnTheFloor/PinOnTheFloor";
import GPSField from "./Fields/GPS/GPSField";
import { formatDate } from "../../../CommonMethods/FormatDate";

// Utility functions moved outside component
const formatName = (name) => name?.replaceAll("_", " ") || "";
const formatQuestionId = (id) => id?.replaceAll("-", "_") || "";

// Separate component for image preview to avoid re-renders
const ImagePreview = React.memo(({ images, onImageClick }) => {
  if (!images || images.length === 0) return "-";

  return (
    <div className="flex flex-wrap gap-2">
      {Object.values(images).map((img, index) => (
        <img
          key={index}
          src={URL.createObjectURL(img)}
          alt="Selected Image"
          data-testid='Photo label name'
          className="w-16 h-16 rounded-lg cursor-pointer"
          onClick={() => onImageClick(img)}
        />
      ))}
    </div>
  );
});

// Separate component for file preview
const FilePreview = React.memo(({ files }) => {
  if (!files || Object.values(files).length === 0) return "-";

  return (
    <ul className="w-full">
      {Object.values(files).map((file, index) => (
        <li
          key={index}
          className="bg-gray-200 my-2 p-2 rounded flex justify-between"
        >
          <span className="truncate w-full max-w-xs">{file.name || file}</span>
        </li>
      ))}
    </ul>
  );
});

// Display field component
const DisplayFieldPreview = React.memo(({ display_type }) => {
  if (display_type.text) {
    return <p className="text-sm text-gray-700">{display_type.text}</p>;
  }
  if (display_type.heading) {
    return <p className="text-sm text-black font-medium">{display_type.heading}</p>;
  }
  if (display_type.image) {
    return <ImageZoomPin imageSrc={display_type.image} />;
  }
  if (display_type.url) {
    return <p className="text-sm text-gray-700">{display_type.url.value}</p>;
  }
  return "-";
});

// Asset location component
const AssetLocationPreview = React.memo(({ value }) => (
  <div className="text-sm text-gray-700">
    <p>{`Site: ${value?.site || "-"}`}</p>
    <p>{`Building: ${value?.location || "-"}`}</p>
    <p>{`Floor: ${value?.level || "-"}`}</p>
  </div>
));

// Standard field component
const StandardFieldPreview = React.memo(({ value, component_type, type }) => {
  if (!value) return "-";
  
  if (type === "date") return formatDate(value);
  
  if (typeof value === "string" || typeof value === "number") {
    return value;
  }
  
  if (typeof value === "object" && value !== null && component_type === "dateTimefield") {
    const timeMatch = JSON.stringify(value).match(/\d{2}:\d{2}:\d{2}/);
    return timeMatch ? timeMatch[0] : "-";
  }
  
  if (Array.isArray(value)) {
    return value.join(", ");
  }
  
  return "-";
});

// Field preview component with memoization
const FieldPreview = React.memo(({ field, conditionalValues, onImageClick }) => {
  const questionId = formatQuestionId(field.question_id);
  const value = conditionalValues[questionId];

  const renderFieldContent = () => {
    switch (field.component_type) {
      case "photofield":
        return <ImagePreview images={value} onImageClick={onImageClick} />;
      case "gpsfield":
        return <GPSField preview />;
      case "floorPlanfield":
        return <ImageZoomPin floorPlan />;
      case "videofield":
      case "filefield":
        return <FilePreview files={value} />;
      case "displayfield":
        return <DisplayFieldPreview display_type={field.display_type} />;
      case "signaturefield":
        return value ? <ImageZoomPin imageSrc={value} /> : "-";
      case "assetLocationfield":
        return <AssetLocationPreview value={value} />;
      default:
        return (
          <p className="text-sm text-gray-700">
            <StandardFieldPreview
              value={value}
              component_type={field.component_type}
              type={field.type}
            />
          </p>
        );
    }
  };

  return (
    <div className="pb-2">
      {field.component_type !== "displayfield" && (
        <h1 className="text-sm text-black font-medium pb-1">
          {formatName(field.question_name)}
        </h1>
      )}
      {renderFieldContent()}
    </div>
  );
});

// Image modal component
const ImageModal = React.memo(({ image, onClose }) => {
  if (!image) return null;
  
  return (
    <div className="modal-overlay absolute rounded-[50px] top-0 flex-col z-20 left-0 w-[356px] h-full bg-white flex justify-center items-center">
      <button
        className="close-icon delay-300 z-[2] items-end text-[20px] ml-auto mr-4 -mt-64 hover:bg-slate-700 transition-transform bg-slate-800 px-1.5 rounded-lg text-white cursor-pointer"
        onClick={onClose}
        aria-label="Close"
      >
        &#10005;
      </button>

      <div className="z-[1]">
        <img
          src={URL.createObjectURL(image)}
          className="w-[374px] h-[600px] bg-white p-2 rounded-lg object-contain"
          alt="Enlarged"
        />
      </div>
    </div>
  );
});

// Main component
export default function PreviewSummary({ conditionalValues, sections }) {
  const [currentImage, setCurrentImage] = useState(null);
  
  // Get questionValue from Redux store
  const questionValue = useSelector((state) => state?.questionValues?.questions);

  const handleImageClick = useCallback((image) => {
    setCurrentImage(image);
  }, []);

  const handleModalClose = useCallback(() => {
    setCurrentImage(null);
  }, []);

  // Enhanced conditional logic evaluation function - similar to Preview.jsx
  const evaluateCondition = useCallback((condition) => {
    if (!condition) return true;

    try {
      // Create a copy of the original logic for processing
      let processedLogic = condition;
      let variableReplacements = {};  // Store variable replacements to track cascading changes

      // Helper function to make cascading replacements
      function trackVariableReplacement(variableName, replacement) {
        variableReplacements[variableName] = replacement;
        // Create a regex that matches the variable name exactly (with word boundaries)
        const variableRegex = new RegExp(`\\b${variableName.replace(/\./g, "\\.")}\\b(?!\\.)`, 'g');
        processedLogic = processedLogic.replace(variableRegex, replacement);
      }

      // Step 1: Parse all distinct variables and their operations in the logic
      const variablesUsed = new Set();

      // Extract all variable names used in the expression
      const variablePattern = /\b([a-zA-Z_][\w]*(?:_[\w]+)+)\b/g;
      let match;
      while ((match = variablePattern.exec(processedLogic)) !== null) {
        variablesUsed.add(match[1]);
      }

      processedLogic = processedLogic.replaceAll("if", '')
      processedLogic = processedLogic.replaceAll("IF", '')
      processedLogic = processedLogic.replaceAll("If", '')

      // Step 2: Handle all "Today" references first
      if (processedLogic.includes('"Today"')) {
        processedLogic = processedLogic.replace(
          /"Today"/g,
          "new Date().toISOString().split('T')[0]"
        );
      }

      // Step 3: Process each variable that might need multiple operations
      for (const variable of variablesUsed) {
        try {
          // Skip if the variable cannot be evaluated
          let variableValue;
          try {
            variableValue = eval(variable);
            if (variableValue === undefined) continue;
          } catch (e) {
            console.warn(`Cannot evaluate variable ${variable}:`, e);
            continue;
          }

          // Create a temporary copy of the logic to search for this variable's operations
          const tempLogic = processedLogic;

          // Step 3.1: Check if this variable has an AddDays or SubtractDays operation
          const addDaysPattern = new RegExp(`${variable.replace(/\./g, "\\.")}\.AddDays\\((\\d+)\\)`, 'g');
          const subtractDaysPattern = new RegExp(`${variable.replace(/\./g, "\\.")}\.SubtractDays\\((\\d+)\\)`, 'g');

          // Handle AddDays operations for this variable
          let addDaysMatch;
          while ((addDaysMatch = addDaysPattern.exec(tempLogic)) !== null) {
            const fullMatch = addDaysMatch[0];
            const days = addDaysMatch[1];

            if (typeof variableValue === 'string') {
              let replacementExpr;
              if (variableValue.includes(':') && (variableValue.includes('-') || variableValue.includes('/'))) {
                replacementExpr = `(new Date(new Date(${variable}.replace(/\\s+[\\d:]+\\s*[APM]*$/, '').replace(/^(\\d{1,2})\\/(\\d{1,2})\\/(\\d{4})$/, '$3-$2-$1')).getTime() + (${days} * 86400000))).toLocaleDateString("en-GB")`;
              } else {
                replacementExpr = `(new Date(new Date(${variable}.replace(/^(\\d{1,2})\\/(\\d{1,2})\\/(\\d{4})$/, '$2/$1/$3')).getTime() + (${days} * 86400000))).toLocaleDateString("en-GB")`;
              }

              // Replace in the actual logic
              processedLogic = processedLogic.replace(fullMatch, replacementExpr);

              // Keep track of this replacement
              const addDaysId = `${variable}_addDays_${days}`;
              try {
                // Evaluate the replacement expression and store it
                const replacementValue = eval(replacementExpr);
                trackVariableReplacement(addDaysId, JSON.stringify(replacementValue));
              } catch (error) {
                console.warn(`Error evaluating AddDays for ${variable}:`, error);
              }
            }
          }

          // Handle SubtractDays operations for this variable
          let subtractDaysMatch;
          while ((subtractDaysMatch = subtractDaysPattern.exec(tempLogic)) !== null) {
            const fullMatch = subtractDaysMatch[0];
            const days = subtractDaysMatch[1];

            if (typeof variableValue === 'string') {
              let replacementExpr;
              if (variableValue.includes(':') && (variableValue.includes('-') || variableValue.includes('/'))) {
                replacementExpr = `(new Date(new Date(${variable}.replace(/\\s+[\\d:]+\\s*[APM]*$/, '').replace(/^(\\d{1,2})\\/(\\d{1,2})\\/(\\d{4})$/, '$3-$2-$1')).getTime() - (${days} * 86400000))).toLocaleDateString("en-GB")`;
              } else {
                replacementExpr = `(new Date(new Date(${variable}.replace(/^(\\d{1,2})\\/(\\d{1,2})\\/(\\d{4})$/, '$2/$1/$3')).getTime() - (${days} * 86400000))).toLocaleDateString("en-GB")`;
              }

              // Replace in the actual logic
              processedLogic = processedLogic.replace(fullMatch, replacementExpr);

              // Keep track of this replacement
              const subtractDaysId = `${variable}_subtractDays_${days}`;
              try {
                // Evaluate the replacement expression and store it
                const replacementValue = eval(replacementExpr);
                trackVariableReplacement(subtractDaysId, JSON.stringify(replacementValue));
              } catch (error) {
                console.warn(`Error evaluating SubtractDays for ${variable}:`, error);
              }
            }
          }
        } catch (error) {
          console.warn(`Error processing variable ${variable}:`, error);
        }
      }

      // Step 4: Now handle complex date method calls like getMonth() on the transformed values
      const dateMethodPattern = /(\w+(?:_\w+)+(?:_addDays_\d+|_subtractDays_\d+)?)\.(getFullYear|getMonth|getDate|getDay|getHours|getMinutes|getSeconds|getMilliseconds|getTime)\(\)/g;

      // Fix for date operations on toLocaleDateString results
      processedLogic = processedLogic.replace(
        /\(new Date\((.*?)\)\)\.toLocaleDateString\("en-GB"\)\.([a-zA-Z]+)\(\)/g,
        (match, dateExpr, method) => {
          return `(new Date(new Date(${dateExpr}))).${method}()`;
        }
      );

      // Handle day name replacements first
      if (processedLogic.includes('getDay()')) {
        const daysMap = {
          Sunday: 0,
          Monday: 1,
          Tuesday: 2,
          Wednesday: 3,
          Thursday: 4,
          Friday: 5,
          Saturday: 6,
        };

        // Replace day names with corresponding numeric values
        processedLogic = processedLogic.replace(
          /getDay\(\)\s*(===|!==)\s*"(.*?)"/g,
          (match, operator, day) => {
            return `getDay() ${operator} ${daysMap[day] ?? `"${day}"`}`;
          }
        );
      }

      // Process all remaining date method calls after AddDays/SubtractDays have been replaced
      let dateMethodMatch;
      const processedDateMethods = new Set(); // Track methods we've already processed
      while ((dateMethodMatch = dateMethodPattern.exec(processedLogic)) !== null) {
        const [fullMatch, variableName, functionName] = dateMethodMatch;

        // Skip if we've already processed this exact match
        if (processedDateMethods.has(fullMatch)) continue;
        processedDateMethods.add(fullMatch);

        let variableValue;
        try {
          // First check if we have a stored replacement for this variable
          if (variableReplacements[variableName]) {
            variableValue = JSON.parse(variableReplacements[variableName]);
          } else {
            // Otherwise try to evaluate directly
            variableValue = eval(variableName);
          }

          if (!variableValue) continue; // Skip if undefined
        } catch (error) {
          console.warn(`Error getting value for ${variableName}:`, error);
          continue;
        }

        // Now process the date method
        if (typeof variableValue === 'string') {
          let replacementExpr;

          if (variableValue.includes(':') && (variableValue.includes('-') || variableValue.includes('/'))) {
            if (["getHours", "getMinutes", "getSeconds", "getMilliseconds", "getTime"].includes(functionName)) {
              if (variableValue.includes("AM") || variableValue.includes("PM")) {
                if (functionName === "getTime") {
                  replacementExpr = `(new Date("1970-01-01 " + ${variableName}.replace(/^([\\d/.-]+)\\s+/, ''))).toTimeString().slice(0,8)`;
                } else {
                  replacementExpr = `new Date("1970-01-01 " + ${variableName}.replace(/^([\\d/.-]+)\\s+/, '')).${functionName}()`;
                }
              } else {
                if (functionName === "getTime") {
                  replacementExpr = `(new Date("1970-01-01T" + ${variableName}.replace(/^([\\d/.-]+)\\s+/, ''))).toTimeString().slice(0,8)`;
                } else {
                  replacementExpr = `new Date("1970-01-01T" + ${variableName}.replace(/^([\\d/.-]+)\\s+/, '')).${functionName}()`;
                }
              }
            } else {
              replacementExpr = `new Date(${variableName}.replace(/\\s+[\\d:]+\\s*[APM]*$/, '').replace(/^(\\d{1,2})\\/(\\d{1,2})\\/(\\d{4})$/, '$2/$1/$3')).${functionName}()`;
            }
          } else {
            if (["getHours", "getMinutes", "getSeconds", "getMilliseconds", "getTime"].includes(functionName)) {
              if (variableValue.includes("AM") || variableValue.includes("PM")) {
                if (functionName === "getTime") {
                  replacementExpr = `new Date("1970-01-01 " + ${variableName}).toTimeString().slice(0,8)`;
                } else {
                  replacementExpr = `new Date("1970-01-01 " + ${variableName}).${functionName}()`;
                }
              } else {
                if (functionName === "getTime") {
                  replacementExpr = `new Date("1970-01-01T" + ${variableName}).toTimeString().slice(0,8)`;
                } else {
                  replacementExpr = `new Date("1970-01-01T" + ${variableName}).${functionName}()`;
                }
              }
            } else {
              replacementExpr = `new Date(${variableName}.replace(/^(\\d{1,2})\\/(\\d{1,2})\\/(\\d{4})$/, '$2/$1/$3')).${functionName}()`;
            }
          }

          // Replace in the actual logic
          processedLogic = processedLogic.replaceAll(fullMatch, replacementExpr);
        }
      }

      // Adjust getMonth() to match JavaScript's 0-based month indexing
      processedLogic = processedLogic.replace(/\.getMonth\(\)/g, ".getMonth()+1");

      // Step 5: Handle multiple choice array values
      let arrayMultiChoiceId = [];
      // Loop through all properties in questionValue
      Object.entries(questionValue || {}).forEach(([key, value]) => {
        // Check if the value is an array
        if (Array.isArray(value)) {
          if (processedLogic?.includes(key.replace(/-/g, "_"))) {
            arrayMultiChoiceId.push(key.replace(/-/g, "_"));
          }
        }
      });

      // Handle Multi-choice
      if ((processedLogic?.includes("===") || processedLogic?.includes("!==") ||
        processedLogic?.includes("==") || processedLogic?.includes("!=")) &&
        arrayMultiChoiceId.length > 0) {

        processedLogic = processedLogic.replace(
          /([a-zA-Z_][\w.?]*(?:\.(?:toUpperCase|toLowerCase|trim)\(\))*)\s*(===|==|!=|!==)\s*['"]([^'"]*)['"]/g,
          (match, path, operator, value) => {
            if (arrayMultiChoiceId.includes(path.replace(/\..*/, ""))) {
              // Handle method calls separately
              let methodCall = "";
              if (path.match(/(\.(toUpperCase|toLowerCase|trim)\(\))+$/)) {
                methodCall = path.match(
                  /(\.(toUpperCase|toLowerCase|trim)\(\))+$/
                )[0];
                path = path.replace(
                  /(\.(toUpperCase|toLowerCase|trim)\(\))+$/,
                  ""
                );
              }

              try {
                let questionArray = eval(path);
                if (Array.isArray(questionArray) && questionArray.length === 1) {
                  // Add back method call if it existed
                  return `${path}[0]${methodCall} ${operator} "${value}"`;
                } else {
                  return `${path}[-1] ${operator} "${value}"`;
                }
              } catch (e) {
                return match;
              }
            } else {
              return match;
            }
          }
        );
      }

      // Step 6: Handle special case for formatDateWithOffset
      if (processedLogic.includes('formatDateWithOffset')) {
        try {
          const spitedValue = processedLogic.split('(')[1].split(')')[0].split(',');
          const formattedResult = formatDateWithOffset(spitedValue[0], spitedValue[1], spitedValue[2]);
          processedLogic = formattedResult ? "true" : "false";
        } catch (error) {
          console.error("Error in formatDateWithOffset:", error);
          return true;
        }
      }

      // Step 7: Handle special case for basic saved_type with date comparisons
      if (processedLogic?.includes('new Date(') &&
        !processedLogic?.includes('formatDateWithOffset')) {
        try {
          const expressions = processedLogic.split(/(?:\|\||&&)/).map(part => part.trim());
          let finalLogic = processedLogic;

          function compareDates(lhs, rhs, operator) {
            const lhsDate = new Date(lhs.split(' ')[0]);
            let date = rhs;
            if (/^\d{2}\/\d{2}\/\d{4}$/.test(rhs)) {
              date = rhs.replace(/(\d{2})\/(\d{2})\/(\d{4})/, "$3-$2-$1")
            }
            const rhsDate = new Date(date);
            switch (operator) {
              case '<': return lhsDate < rhsDate;
              case '<=': return lhsDate <= rhsDate;
              case '>': return lhsDate > rhsDate;
              case '>=': return lhsDate >= rhsDate;
              case '==': return lhsDate.getTime() === rhsDate.getTime();
              case '!=': return lhsDate.getTime() !== rhsDate.getTime();
              default: return false;
            }
          }

          for (const expr of expressions) {
            // Match LHS, operator, RHS
            const match = expr.match(/(.*?)\s*(<=|>=|<|>|==|!=)\s*(.*)/);

            if (match) {
              let [, lhs, operator, rhs] = match;

              // Get actual values
              let lhsValue, rhsValue;
              try {
                lhsValue = eval(lhs); // fallback to eval if not in object
                rhsValue = eval(rhs); // RHS is likely a dynamic expression like new Date...

                const result = compareDates(lhsValue, rhsValue, operator);
                if (result) {
                  // Replace the expression in original string with true/false
                  finalLogic = finalLogic.replace(expr, result);
                }

              } catch (error) {
                console.error("Error evaluating expression:", error);
              }
            }
          }

          processedLogic = finalLogic;
        } catch (error) {
          console.error("Error in date comparison logic:", error);
          return true;
        }
      }

      // Final step: Evaluate the processed logic using new Function
      try {
        return new Function('return ' + processedLogic)() !== false;
      } catch (error) {
        console.warn("Error evaluating final conditional logic:", error, "Logic was:", processedLogic);
        return true; // Default to showing the field if evaluation fails
      }
    } catch (error) {
      console.error("Error in conditional logic evaluation:", error);
      return true;
    }
  }, [questionValue]);

  // Enhanced setXDate function for formatDateWithOffset
  const setXDate = useCallback((conditional_logic) => {
    function formatDateWithOffset(formatteDate, value, question_name) {
      let [day, month, year] = formatteDate.split(`'`)[1].split('/').map(Number);
      let date = new Date(year, month - 1, day + 1 + Number(value)).toISOString().split('T')[0];
      return conditionalValues[question_name.trim()] === date;
    }
    
    try {
      const spitedValue = conditional_logic.split('(')[1].split(')')[0].split(',');
      return formatDateWithOffset(spitedValue[0], spitedValue[1], spitedValue[2]);
    } catch (error) {
      console.error("Error in setXDate:", error);
      return true;
    }
  }, [conditionalValues]);

  // Memoized section filtering
  const filteredSections = useMemo(() => {
    if (!sections) return [];
    
    return sections.map(section => ({
      ...section,
      shouldRender: evaluateCondition(section.section_conditional_logic),
      pages: section.pages?.map(page => ({
        ...page,
        shouldRender: evaluateCondition(page.page_conditional_logic),
        questions: page.questions?.filter(field => 
          field.options.visible && field.conditional_logic.includes('formatDateWithOffset') ? setXDate(field.conditional_logic) : evaluateCondition(field.conditional_logic)
        )
      })).filter(page => page.shouldRender && page.questions?.length > 0)
    })).filter(section => section.shouldRender && section.pages?.length > 0);
  }, [sections, evaluateCondition, setXDate]);

  return (
    <>
      <h2
        className="text-2xl font-bold text-[#2B333B] items-center w-full flex justify-center mb-4"
        data-testid="preview-summary"
      >
        Summary
      </h2>

      {filteredSections.map((section, sectionIndex) => (
        <div key={`section-${sectionIndex + 1}`}>
          <h1 className="text-xl font-bold text-[#2B333B] mb-4" data-testid={`summary-${section.section_name.toLowerCase().replaceAll(' ', '-')}`}>
            {formatName(section.section_name)}
          </h1>
          
          {section.pages?.map((page, pageIndex) => (
            <div
              key={`page-${pageIndex}`}
              className="mt-3 mb-3 bg-white rounded-xl py-4 px-3 gap-1"
            >
              <label
                data-testid={`summary-${section.section_name.toLowerCase().replaceAll(' ', '-')}-${page.page_name.toLowerCase().replaceAll(' ', '-')}`}
                className="font-medium pb-1 text-base text-black overflow-hidden break-all block w-full max-w-[85%]"
              >
                {formatName(page.page_name)}
              </label>
              
              {page.questions?.map((field, fieldIndex) => (
                <FieldPreview
                  key={`field-${fieldIndex}`}
                  field={field}
                  conditionalValues={conditionalValues}
                  onImageClick={handleImageClick}
                />
              ))}
            </div>
          ))}
        </div>
      ))}

      {currentImage && (
        <ImageModal image={currentImage} onClose={handleModalClose} />
      )}
    </>
  );
}